import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import * as api from "../services/api";

import logo from "../images/avrnhh-transparent.png";
import LoginModal from "./LoginModal";
import ProfileMenu from "./ProfileMenu";
import { convertGoogleDriveUrlForDisplay } from "../utils/imageUtils";
import "./Header.css";

export default function Header({ theme, setTheme }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { cartCount } = useCart();
  const { isAuthenticated } = useAuth();
  const [userLocation, setUserLocation] = useState("Detecting location...");
  const [serviceable, setServiceable] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceTimerRef = useRef(null);
  
  // Check if we're on the cart or checkout page
  const isCartPage = location.pathname === '/cart';
  const isCheckoutPage = location.pathname === '/checkout';

  const [isLoginOpen, setIsLoginOpen] = useState(false);

  // Get search query from URL if on search page
  useEffect(() => {
    if (location.pathname === '/search') {
      const params = new URLSearchParams(location.search);
      const query = params.get('q') || '';
      setSearchQuery(query);
    } else {
      setSearchQuery('');
    }
  }, [location]);

  const handleLoginClick = () => {
    setIsLoginOpen(true); // open modal
  };

  const handleCloseModal = () => {
    setIsLoginOpen(false); // close modal
  };

  // Fetch search suggestions
  const fetchSuggestions = useCallback(async (query) => {
    if (!query.trim() || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      setLoadingSuggestions(true);
      const results = await api.getProducts({ search: query.trim() });
      // Limit to 5 suggestions for better UX
      setSuggestions(results.slice(0, 5));
      setShowSuggestions(true);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  // Debounced search suggestions
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (searchQuery.trim().length >= 2) {
      debounceTimerRef.current = setTimeout(() => {
        fetchSuggestions(searchQuery);
      }, 300); // 300ms debounce
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, fetchSuggestions]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (query) {
      setShowSuggestions(false);
      navigate(`/search?q=${encodeURIComponent(query)}`);
    }
  };

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
    setSelectedIndex(-1);
  };

  const handleSearchKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') {
        handleSearch(e);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        } else {
          handleSearch(e);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSuggestionClick = (product) => {
    if (product._id) {
      setShowSuggestions(false);
      setSearchQuery('');
      navigate(`/product/${product._id}`);
    } else {
      // Fallback to search if no product ID
      setSearchQuery(product.title || product.name);
      setShowSuggestions(false);
      navigate(`/search?q=${encodeURIComponent(product.title || product.name)}`);
    }
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0 && searchQuery.trim().length >= 2) {
      setShowSuggestions(true);
    }
  };

  useEffect(() => {
    if (isLoginOpen) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
  }, [isLoginOpen]);
  

  useEffect(() => {
    // Simulated geolocation and serviceability check
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLocation("Ranchi, Jharkhand");
          setServiceable(true);
        },
        () => {
          setUserLocation("Location not available");
          setServiceable(false);
        }
      );
    } else {
      setUserLocation("Location not available");
      setServiceable(false);
    }
  }, []);

  return (
    <>
      {/* Show Login Modal fullscreen overlay */}
      {isLoginOpen && (
        <div className="login-overlay">
          <LoginModal onClose={handleCloseModal} />
        </div>
      )}
  
      {/* Header is visible only if modal is closed */}
      {!isLoginOpen && (
        <>
          <header className="header">
            {/* Desktop / Tablet */}
            <div className="header-desktop">
              <div className="left">
                <img 
                  src={logo} 
                  alt="Avrnhh" 
                  className="logo" 
                  onClick={() => navigate('/')}
                  style={{ cursor: 'pointer' }}
                />
              </div>
  
              <div className="center">
                <div className="location">
                  <span className="location-icon">📍</span>
                  <div className="location-info">
                    <span className="location-label">Deliver to</span>
                    <span className="location-name">
                      {userLocation}{" "}
                      <span
                        className={`status ${
                          serviceable ? "serviceable" : "unserviceable"
                        }`}
                      >
                        {serviceable === null
                          ? "Checking..."
                          : serviceable
                          ? "(Serviceable)"
                          : "(Unserviceable)"}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
  
              <div className="right">
                <div className="search-box-wrapper">
                  <div className="search-box">
                    <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', width: '100%', position: 'relative' }}>
                      <input 
                        ref={searchInputRef}
                        type="text" 
                        placeholder="Search for products..." 
                        value={searchQuery}
                        onChange={handleSearchInputChange}
                        onKeyDown={handleSearchKeyDown}
                        onFocus={handleInputFocus}
                        autoComplete="off"
                      />
                      <button type="submit" className="search-btn">🔍</button>
                    </form>
                  </div>
                  {showSuggestions && (
                    <div ref={suggestionsRef} className="search-suggestions">
                      {loadingSuggestions ? (
                        <div className="suggestion-item loading">Loading suggestions...</div>
                      ) : suggestions.length > 0 ? (
                        suggestions.map((product, index) => (
                          <div
                            key={product._id}
                            className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
                            onClick={() => handleSuggestionClick(product)}
                            onMouseEnter={() => setSelectedIndex(index)}
                          >
                            <div className="suggestion-content">
                              <div className="suggestion-title">{product.title || product.name}</div>
                              {product.category && (
                                <div className="suggestion-category">
                                  {product.category.name || product.category}
                                </div>
                              )}
                            </div>
                            {product.images && product.images.length > 0 && (
                              <div className="suggestion-image">
                                <img 
                                  src={convertGoogleDriveUrlForDisplay(product.images[0])} 
                                  alt={product.title || product.name}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="suggestion-item no-results">No suggestions found</div>
                      )}
                    </div>
                  )}
                </div>
                {isAuthenticated ? (
                  <ProfileMenu />
                ) : (
                  <button className="login-btn" onClick={handleLoginClick}>
                    Login
                  </button>
                )}
                <button 
                  className="cart-btn"
                  onClick={() => navigate('/cart')}
                  style={{ cursor: 'pointer' }}
                >
                  🛒 <span className="cart-count">{cartCount}</span>
                </button>
                {/* Light and Dark mode toggle button for desktop */}
                {/* <button
                  className="theme-toggle"
                  onClick={() =>
                    setTheme(theme === "light" ? "dark" : "light")
                  }
                >
                  {theme === "light" ? "🌙" : "☀️"}
                </button> */}
              </div>
            </div>
  
            {/* Mobile Header */}
            <div className="header-mobile">
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div className="mobile-top">
                  <img 
                    src={logo} 
                    alt="Avrnhh" 
                    className="logo" 
                    onClick={() => navigate('/')}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
                <div className="mobile-icons">
                  {isAuthenticated ? (
                    <ProfileMenu />
                  ) : (
                    <button
                      className="icon-btn"
                      title="Login"
                      onClick={handleLoginClick}
                    >
                      👤
                    </button>
                  )}
                </div>
              </div>
  
              <div className="mobile-location">
                <span className="location-icon">📍</span>
                <span className="location-text">
                  {userLocation}{" "}
                  <span
                    className={`status ${
                      serviceable ? "serviceable" : "unserviceable"
                    }`}
                  >
                    {serviceable === null
                      ? "Checking..."
                      : serviceable
                      ? "(Serviceable)"
                      : "(Unserviceable)"}
                  </span>
                </span>
              </div>
  
              <div className="mobile-search-wrapper">
                <div className="mobile-search">
                  <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', width: '100%', position: 'relative' }}>
                    <input 
                      ref={searchInputRef}
                      type="text" 
                      placeholder="Search for products..." 
                      value={searchQuery}
                      onChange={handleSearchInputChange}
                      onKeyDown={handleSearchKeyDown}
                      onFocus={handleInputFocus}
                      autoComplete="off"
                    />
                    <button type="submit" className="search-btn">🔍</button>
                  </form>
                </div>
                {showSuggestions && (
                  <div ref={suggestionsRef} className="search-suggestions mobile-suggestions">
                    {loadingSuggestions ? (
                      <div className="suggestion-item loading">Loading suggestions...</div>
                    ) : suggestions.length > 0 ? (
                      suggestions.map((product, index) => (
                        <div
                          key={product._id}
                          className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
                          onClick={() => handleSuggestionClick(product)}
                          onMouseEnter={() => setSelectedIndex(index)}
                        >
                          <div className="suggestion-content">
                            <div className="suggestion-title">{product.title || product.name}</div>
                            {product.category && (
                              <div className="suggestion-category">
                                {product.category.name || product.category}
                              </div>
                            )}
                          </div>
                          {product.images && product.images.length > 0 && (
                            <div className="suggestion-image">
                              <img 
                                src={product.images[0]} 
                                alt={product.title || product.name}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="suggestion-item no-results">No suggestions found</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </header>
  
          {/* Fixed Cart Bar on Mobile - Hide on cart and checkout pages */}
          {cartCount > 0 && !isCartPage && !isCheckoutPage && (
            <div className="mobile-cart-bar">
              <div className="cart-summary">
                <div>
                  🛒 <strong>{cartCount}</strong> item
                  {cartCount > 1 ? "s" : ""} in cart
                </div>
                <button 
                  className="view-cart-btn"
                  onClick={() => navigate('/cart')}
                >
                  View Cart
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
  
  
}
